import { Controller } from "@hotwired/stimulus"

// Draft migration banner on /projects index.
//
// If the user has a non-empty anonymous draft in localStorage
// (`plot-planner:draft-v1`, written by autosave while they edited
// /editor without logging in), offer to save it as a new cloud project.
//
// Three actions:
//   - Save: POST /projects with {name, state} seeded from the draft, then
//     clear the draft and navigate to the new project.
//   - Delete: remove the draft from localStorage (no server call).
//   - Keep: hide the banner for this visit, leave draft intact for /editor.
const DRAFT_KEY = "plot-planner:draft-v1"
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

export default class extends Controller {
  static targets = ["banner", "summary", "saveButton", "deleteButton"]

  connect() {
    this._draft = this.#readDraft()
    if (!this._draft) return // no valid draft — stay hidden
    if (!this.hasBannerTarget || !this.hasSummaryTarget) return
    this.#renderSummary(this._draft)
    this.bannerTarget.classList.remove("hidden")
  }

  async save() {
    if (!this._draft) return
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = true
      this.saveButtonTarget.textContent = "Сохраняется…"
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const name = this.#deriveName(this._draft)

    try {
      // Single JSON POST — creates the project WITH state in one round-trip.
      // Simpler than create-then-autosave because `fetch` with redirect:"manual"
      // produces an opaque response whose Location header is inaccessible.
      const res = await fetch("/projects", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify({
          project: {
            name,
            state: {
              plot: this._draft.plot,
              objects: this._draft.objects,
              norms: this._draft.norms,
              ui: this._draft.ui,
            },
          },
        }),
      })
      if (!res.ok) throw new Error(`create failed ${res.status}`)
      const data = await res.json()
      if (!data.id) throw new Error("no id in response")

      try { localStorage.removeItem(DRAFT_KEY) } catch (e) {}
      window.location.href = `/projects/${data.id}/edit`
    } catch (e) {
      if (this.hasSaveButtonTarget) {
        this.saveButtonTarget.disabled = false
        this.saveButtonTarget.textContent = "Сохранить как новый проект"
      }
      alert("Не удалось сохранить черновик. Попробуйте ещё раз или нажмите «Оставить».")
    }
  }

  delete() {
    if (!confirm("Удалить локальный черновик без сохранения?")) return
    try { localStorage.removeItem(DRAFT_KEY) } catch (e) {}
    if (this.hasBannerTarget) this.bannerTarget.classList.add("hidden")
  }

  dismiss() {
    if (this.hasBannerTarget) this.bannerTarget.classList.add("hidden")
  }

  #readDraft() {
    let raw
    try { raw = localStorage.getItem(DRAFT_KEY) } catch (e) { return null }
    if (!raw) return null
    let payload
    try { payload = JSON.parse(raw) } catch (e) { return null }
    if (!payload || payload.version !== 1) return null
    if (!payload.savedAt || Date.now() - payload.savedAt > MAX_AGE_MS) return null
    if (!payload.plot && (!payload.objects || payload.objects.length === 0)) return null
    return payload
  }

  #renderSummary(draft) {
    const parts = []
    const n = Array.isArray(draft.objects) ? draft.objects.length : 0
    if (n > 0) parts.push(`${n} ${ruPlural(n, "объект", "объекта", "объектов")}`)
    if (draft.plot?.polygonM) {
      const area = polygonAreaM2(draft.plot.polygonM)
      if (area > 0) parts.push(`${Math.round(area)} м²`)
    }
    const when = new Date(draft.savedAt)
    parts.push(when.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" }))
    this.summaryTarget.textContent = parts.join(" · ")
  }

  #deriveName(draft) {
    if (draft.plot?.address) return draft.plot.address
    return `Участок ${new Date(draft.savedAt).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" })}`
  }
}

function ruPlural(n, one, few, many) {
  const mod100 = Math.abs(n) % 100
  if (mod100 >= 11 && mod100 <= 14) return many
  const mod10 = mod100 % 10
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return few
  return many
}

function polygonAreaM2(polygon) {
  if (!Array.isArray(polygon) || polygon.length < 3) return 0
  let sum = 0
  for (let i = 0; i < polygon.length; i++) {
    const [x1, y1] = polygon[i]
    const [x2, y2] = polygon[(i + 1) % polygon.length]
    sum += x1 * y2 - x2 * y1
  }
  return Math.abs(sum) / 2
}
